import { evaluateAggregate, evaluateNativeExpression, inspectTypedExpressionStructure, NativeExpressionError, TYPED_EXPRESSIONS_CAPABILITY } from "./nativeExpression.mjs";
import { isNativeCalendarDate as calendar } from "./nativeScalar.mjs";
import { compileTypedReportTargets, validateTypedReportTables } from "./nativeTypedReport.mjs";
import { compileReportParameters, REPORT_PARAMETERS_CAPABILITY, validatePresentationFilters } from "./nativeParameters.mjs";

export const CONDITIONAL_RULES_CAPABILITY = "conditional-rules-v1";
export const NATIVE_CAPABILITIES = Object.freeze({
  schemas: Object.freeze(["reports-v3.0", "reports-v3.1"]),
  recognized: Object.freeze([CONDITIONAL_RULES_CAPABILITY, TYPED_EXPRESSIONS_CAPABILITY, REPORT_PARAMETERS_CAPABILITY]),
  validation: Object.freeze([CONDITIONAL_RULES_CAPABILITY, TYPED_EXPRESSIONS_CAPABILITY, REPORT_PARAMETERS_CAPABILITY]),
  rendering: Object.freeze([]),
  authoring: Object.freeze([]),
});
export const CONDITION_LIMITS = Object.freeze({
  depth: 16, nodes: 256, operands: 16, literalLength: 1024, pathLength: 256,
  rulesPerTarget: 16, rules: 256, definitionNodes: 16384, work: 2000000,
});
const scopes = ["document", "dataset", "group", "detail", "cell"];
const types = ["number", "string", "boolean", "date", "timestamp"];
const comparisons = ["eq", "neq", "gt", "gte", "lt", "lte"];
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const fieldType = (fields, scope, path) => fields?.[scope] && own(fields[scope], path) ? fields[scope][path] : undefined;
const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const safeId = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
const objectPrototypes = new WeakSet([Object.prototype]);
const arrayPrototypes = new WeakSet([Array.prototype]);
function standardPrototype(prototype, standard, cache) {
  if (prototype === null || typeof prototype !== "object") return false;
  if (cache.has(prototype)) return true;
  const names = Object.getOwnPropertyNames(standard);
  if (Object.getOwnPropertyNames(prototype).length !== names.length) return false;
  for (const key of names) {
    const left = Object.getOwnPropertyDescriptor(prototype, key);
    const right = Object.getOwnPropertyDescriptor(standard, key);
    if (!left || left.enumerable !== right.enumerable || own(left, "value") !== own(right, "value")) return false;
    for (const member of own(right, "value") ? ["value"] : ["get", "set"]) {
      if (typeof left[member] !== typeof right[member]) return false;
      if (typeof right[member] === "function" ? Function.prototype.toString.call(left[member]) !== Function.prototype.toString.call(right[member]) : left[member] !== right[member]) return false;
    }
  }
  cache.add(prototype);
  return true;
}

export class NativeConditionError extends Error {
  constructor(code, path, message, identity = {}) {
    super(message);
    this.name = "NativeConditionError";
    this.code = code;
    this.path = path;
    this.status = code.endsWith("_LIMIT") ? 413 : 400;
    for (const key of ["ruleId", "elementId", "sourceIndex"]) if (identity[key] !== undefined) this[key] = identity[key];
  }
}
function fail(code, path, message) { throw new NativeConditionError(code, path, message); }
function object(value, path) {
  const prototype = isRecord(value) ? Object.getPrototypeOf(value) : undefined;
  if (!isRecord(value) || (prototype !== null && (Object.getPrototypeOf(prototype) !== null || !standardPrototype(prototype, Object.prototype, objectPrototypes)))) fail("NATIVE_CONDITION_INVALID", path, "Expected a plain declarative object.");
  for (const key of Object.getOwnPropertyNames(value)) {
    if (!own(Object.getOwnPropertyDescriptor(value, key), "value")) fail("NATIVE_CONDITION_INVALID", path, "Accessor properties are not allowed.");
  }
  return value;
}
function dataArray(value, path, max = 200000) {
  if (!Array.isArray(value) || !standardPrototype(Object.getPrototypeOf(value), Array.prototype, arrayPrototypes)) fail("NATIVE_CONDITION_INVALID", path, "Expected a plain declarative array.");
  if (value.length > max) fail("NATIVE_CONDITION_LIMIT", path, "Conditional array limit exceeded.");
  for (const key of Object.getOwnPropertyNames(value)) {
    if ((key !== "length" && !/^(0|[1-9]\d*)$/.test(key)) || !own(Object.getOwnPropertyDescriptor(value, key), "value")) fail("NATIVE_CONDITION_INVALID", path, "Accessor or executable array properties are unsupported.");
  }
  for (let index = 0; index < value.length; index++) if (!own(value, index)) fail("NATIVE_CONDITION_INVALID", path, "Sparse declarative arrays are unsupported.");
  return value;
}
function keys(value, allowed, path) {
  object(value, path);
  if (Object.keys(value).some((key) => !allowed.includes(key))) fail("NATIVE_CONDITION_INVALID", path, "Unsupported conditional property.");
}
function limitOptions(options = {}) {
  const limits = { ...CONDITION_LIMITS };
  for (const [key, value] of Object.entries(options)) {
    if (!own(limits, key) || !Number.isSafeInteger(value) || value < 1 || value > limits[key]) fail("NATIVE_CONDITION_LIMIT", "$.limits", "Condition limits may only reduce the supported positive budgets.");
    limits[key] = value;
  }
  return limits;
}
function safePath(path, location, limits = CONDITION_LIMITS) {
  if (typeof path !== "string" || path.length > limits.pathLength || !/^[A-Za-z_][A-Za-z0-9_-]*(?:\.[A-Za-z_][A-Za-z0-9_-]*)*$/.test(path)
    || path.split(".").some((part) => ["__proto__", "prototype", "constructor"].includes(part))) fail("NATIVE_CONDITION_FIELD", location, "Expected a bounded safe field path.");
}
function arithmeticArgs(node) {
  return own(node, "args") ? node.args : [node.left, node.right].filter((arg) => arg !== undefined);
}
function readPath(source, path, location) {
  let value = source;
  for (const part of path.split(".")) {
    if (!isRecord(value)) fail("NATIVE_CONDITION_FIELD", location, "Condition field is unavailable in its declared scope.");
    object(value, location);
    const descriptor = Object.getOwnPropertyDescriptor(value, part);
    if (!descriptor || !own(descriptor, "value") || descriptor.value === undefined) fail("NATIVE_CONDITION_FIELD", location, "Condition field is unavailable in its declared scope.");
    value = descriptor.value;
  }
  return value;
}
function typed(value, declared, path, limits) {
  if (value === null) return { type: "null", value };
  const type = declared || typeof value;
  if (type === "number" && (typeof value !== "number" || !Number.isFinite(value))) fail("NATIVE_CONDITION_TYPE", path, "Condition requires a finite JSON number.");
  else if (type === "string" && (typeof value !== "string" || value.length > limits.literalLength)) fail("NATIVE_CONDITION_TYPE", path, "Condition requires a bounded string.");
  else if (type === "boolean" && typeof value !== "boolean") fail("NATIVE_CONDITION_TYPE", path, "Condition requires a boolean.");
  else if (type === "date" && !calendar(value)) fail("NATIVE_CONDITION_TYPE", path, "Condition requires a valid YYYY-MM-DD calendar date.");
  else if (type === "timestamp") {
    const match = typeof value === "string" && /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?Z$/.exec(value);
    if (!match || !calendar(match[1]) || Number(match[2]) > 23 || Number(match[3]) > 59 || Number(match[4]) > 59 || !Number.isFinite(Date.parse(value))) fail("NATIVE_CONDITION_TYPE", path, "Condition requires a valid UTC timestamp.");
    return { type, value: Date.parse(value) };
  } else if (!types.includes(type)) fail("NATIVE_CONDITION_TYPE", path, "Condition values must be typed scalars.");
  return { type, value };
}

export function parseClientCapabilities(header) {
  if (header === undefined || header === "") return [];
  if (typeof header !== "string" || header.length > 1024) fail("UNSUPPORTED_REPORT_PROFILE", "$.clientCapabilities", "Malformed report capability declaration.");
  const values = header.split(",").map((item) => item.trim());
  if (new Set(values).size !== values.length || values.some((value) => !NATIVE_CAPABILITIES.recognized.includes(value))) fail("UNSUPPORTED_REPORT_PROFILE", "$.clientCapabilities", "Unsupported or duplicate caller capability.");
  return values;
}
export function rendererCapabilityHeaders() {
  return {
    "X-Reports-Recognized-Features": NATIVE_CAPABILITIES.recognized.join(","),
    "X-Reports-Supported-Features": NATIVE_CAPABILITIES.rendering.join(","),
  };
}
export function inspectReportCapabilities(payload) {
  object(payload, "$");
  if (isRecord(payload.renderer)) object(payload.renderer, "$.renderer");
  const editor = isRecord(payload.renderer) && isRecord(payload.renderer.layout);
  if (editor && (own(payload, "layout") || ["layout", "schemaVersion", "conditions"].some((key) => own(payload.renderer.topLevel || {}, key)))) fail("UNSUPPORTED_REPORT_PROFILE", "$.renderer", "Ambiguous editor/source envelope is unsupported.");
  const schemaVersion = editor ? payload.sourceSchemaVersion : payload.schemaVersion;
  if (schemaVersion != null && !NATIVE_CAPABILITIES.schemas.includes(schemaVersion)) fail("UNSUPPORTED_REPORT_SCHEMA", "$.schemaVersion", "Unsupported report schema version.");
  const layout = editor ? payload.renderer.layout : payload.layout;
  if (own(payload, "parameters") || own(payload, "presentationFilters") || (editor && ["parameters", "presentationFilters"].some(key => own(payload.renderer.topLevel || {}, key)))) fail("UNSUPPORTED_REPORT_PROFILE", "$", "Parameter declarations and presentation filters require canonical layout carriers.");
  if (isRecord(layout)) object(layout, "$.layout");
  if (schemaVersion === "reports-v3.1" && (!isRecord(layout) || !Array.isArray(layout.bands))) fail("UNSUPPORTED_REPORT_PROFILE", "$.layout.bands", "Native reports-v3.1 requires layout.bands; legacy fallback is not permitted.");
  if (schemaVersion !== "reports-v3.1" && isRecord(layout) && own(layout, "bands")) fail("UNSUPPORTED_REPORT_PROFILE", "$.schemaVersion", "Native layout.bands requires reports-v3.1; schema omission or downgrade is unsupported.");
  const required = isRecord(layout) && own(layout, "requiredFeatures") ? layout.requiredFeatures : [];
  if (Array.isArray(required)) dataArray(required, "$.layout.requiredFeatures", 32);
  if (!Array.isArray(required) || required.some((value) => typeof value !== "string" || !NATIVE_CAPABILITIES.recognized.includes(value)) || new Set(required).size !== required.length) {
    fail("UNSUPPORTED_REPORT_PROFILE", "$.layout.requiredFeatures", "Unknown, malformed or duplicate mandatory report capability.");
  }
  let nodes = 0;
  let conditional = own(payload, "conditions");
  let typed = false;
  let parameterized = false;
  const typedTables = new Set();
  const typedTexts = new Map();
  function typedFailure(action) {
    try { return action(); }
    catch (error) {
      if (!(error instanceof NativeExpressionError)) throw error;
      throw new NativeConditionError(error.code, error.path, error.message);
    }
  }
  const visit = (value, depth, mode = "", table, expression = false, band, text) => {
    if (++nodes > 200000 || depth > 32) fail("NATIVE_CONDITION_LIMIT", "$.layout", "Report layout complexity limit exceeded.");
    if (!value || typeof value !== "object") return;
    if (Array.isArray(value)) dataArray(value, "$.layout");
    else object(value, "$.layout");
    if (!Array.isArray(value) && (own(value, "parameters") || own(value, "presentationFilters") || (expression && own(value, "param")))) {
      parameterized = true;
      if (!required.includes(REPORT_PARAMETERS_CAPABILITY)) fail("UNSUPPORTED_REPORT_PROFILE", "$.layout.requiredFeatures", "Parameter structures require report-parameters-v1.");
      if (mode !== "layout") fail("NATIVE_PARAMETER_INVALID", "$.layout", "Parameter declarations and presentation filters belong only to the native layout.");
    }
    if (!Array.isArray(value) && (own(value, "typed") || (expression && (own(value, "call") || own(value, "calc") || own(value, "param"))))) {
      typed = true;
      if (!required.includes(TYPED_EXPRESSIONS_CAPABILITY)) fail("UNSUPPORTED_REPORT_PROFILE", "$.layout.requiredFeatures", "Typed expression structures require typed-expressions-v1.");
      if (!["typedRoot", "typedTextRoot", "typedFilterRoot"].includes(mode) || !own(value, "typed")) fail("NATIVE_EXPRESSION_TARGET", "$.layout", "Typed expressions require an approved direct native table-column, text or filter carrier.");
      const inspected = typedFailure(() => inspectTypedExpressionStructure(value));
      if (inspected.hasParameters) {
        parameterized = true;
        if (!required.includes(REPORT_PARAMETERS_CAPABILITY)) fail("UNSUPPORTED_REPORT_PROFILE", "$.layout.requiredFeatures", "Parameter references require report-parameters-v1.");
      }
      nodes += inspected.documentNodes - 1;
      if (nodes > 200000) fail("NATIVE_CONDITION_LIMIT", "$.layout", "Report layout complexity limit exceeded.");
      if (mode === "typedTextRoot") typedTexts.set(text, { element: text, band });
      else if (mode === "typedRoot") typedTables.add(table);
      return;
    }
    for (const [key, child] of Object.entries(value)) {
      if (["__proto__", "prototype", "constructor"].includes(key)) fail("NATIVE_CONDITION_FIELD", "$.layout", "Unsafe report layout property.");
      if (key === "conditions") conditional = true;
      let next = "";
      if (mode === "layout" && key === "bands") next = "bands";
      if (mode === "bands" && Array.isArray(value)) next = "band";
      if (mode === "band" && key === "elements") next = "elements";
      if (mode === "elements" && Array.isArray(value)) next = "element";
      if (mode === "element" && value.type === "table" && key === "columns") next = "columns";
      if (mode === "columns" && Array.isArray(value)) next = "column";
      if (mode === "column" && key === "expr") next = "typedRoot";
      if (mode === "element" && value.type === "text" && key === "expr") next = "typedTextRoot";
      if (mode === "layout" && key === "presentationFilters") next = "filters";
      if (mode === "filters" && Array.isArray(value)) next = "filter";
      if (mode === "filter" && key === "when") next = "typedFilterRoot";
      visit(child, depth + 1, next, mode === "element" && value.type === "table" ? value : table,
        expression || ["expr", "calculation", "expression", "when"].includes(key),
        mode === "band" ? value : band, mode === "element" && value.type === "text" ? value : text);
    }
  };
  visit(layout, 0, "layout");
  if (editor) visit(payload.bands, 0);
  if ((conditional || required.length) && schemaVersion !== "reports-v3.1") fail("UNSUPPORTED_REPORT_PROFILE", "$.schemaVersion", "Conditional capabilities require native reports-v3.1.");
  if (conditional && !required.includes(CONDITIONAL_RULES_CAPABILITY)) fail("UNSUPPORTED_REPORT_PROFILE", "$.layout.requiredFeatures", "Conditional members require conditional-rules-v1.");
  if (parameterized || required.includes(REPORT_PARAMETERS_CAPABILITY)) typedFailure(() => {
    compileReportParameters(layout.parameters);
    const filters = validatePresentationFilters({ ...payload, layout });
    for (const filter of filters) {
      const table = layout.bands.flatMap(band => band.elements).find(element => element.id === filter.tableId);
      typedTables.add(table);
    }
  });
  if (typed || typedTables.size) {
    typedFailure(() => validateTypedReportTables({ ...payload, layout }, typedTables, [...typedTexts.values()]));
  }
  return { schemaVersion, requiredFeatures: [...required], hasConditionalContent: conditional, hasTypedContent: typed, recognized: true, validationSupported: true, renderingSupported: required.length === 0, authoringSupported: required.length === 0 };
}
export function assertReportAdmission(payload, operation = "render", callerHeader) {
  if (!["validate", "render", "edit", "save", "copy", "import", "serialize", "sample"].includes(operation)) fail("UNSUPPORTED_REPORT_PROFILE", "$.operation", "Unsupported report operation.");
  const result = inspectReportCapabilities(payload);
  if (callerHeader !== undefined) parseClientCapabilities(callerHeader);
  if (operation !== "validate" && result.requiredFeatures.length) fail("UNSUPPORTED_REPORT_PROFILE", "$.layout.requiredFeatures", "Mandatory native features are recognized for validation only. Authoring, saves and Preview/PDF/Excel rendering remain unavailable until later milestones and backend acceptance.");
  return result;
}
export function reportAdmission(payload, operation = "render") {
  try { return { admitted: true, capabilities: assertReportAdmission(payload, operation) }; }
  catch (error) {
    if (!(error instanceof NativeConditionError)) throw error;
    return { admitted: false, error: diagnostic(error) };
  }
}
function diagnostic(error) {
  return { code: error.code, path: error.path.slice(0, 2048), message: error.message, ...Object.fromEntries(["ruleId", "elementId", "sourceIndex"].filter((key) => error[key] !== undefined).map((key) => [key, error[key]])) };
}

function predicateValidator(options = {}) {
  const limits = limitOptions(options.limits);
  const allowedScopes = options.allowedScopes || scopes;
  let nodes = 0;
  function charge(depth, path) {
    if (++nodes > limits.nodes || depth > limits.depth) fail("NATIVE_CONDITION_LIMIT", path, "Predicate depth or node limit exceeded.");
  }
  function arithmetic(node, path, depth = 1, references) {
    charge(depth, path);
    object(node, path);
    if (own(node, "const")) {
      keys(node, ["const"], path);
      if (typeof node.const !== "number" || !Number.isFinite(node.const)) fail("NATIVE_CONDITION_TYPE", path, "Arithmetic constants must be finite numbers.");
    } else if (own(node, "field")) {
      keys(node, ["field"], path); safePath(node.field, path, limits);
      references?.add(node.field);
    } else {
      keys(node, ["op", "args", "left", "right"], path);
      if (!["add", "sub", "mul", "div"].includes(node.op) || (own(node, "args") && (own(node, "left") || own(node, "right")))) fail("NATIVE_CONDITION_DEPENDENCY", path, "Unsupported or ambiguous arithmetic dependency.");
      if (!own(node, "args") && (!own(node, "left") || (node.op !== "sub" && !own(node, "right")))) fail("NATIVE_CONDITION_DEPENDENCY", path, "Arithmetic requires explicit operands.");
      const args = arithmeticArgs(node);
      if (!Array.isArray(args) || args.length < (node.op === "sub" ? 1 : 2) || args.length > limits.operands || (node.op === "div" && args.length !== 2)) fail("NATIVE_CONDITION_DEPENDENCY", path, "Invalid arithmetic dependency operands.");
      dataArray(args, path, limits.operands);
      args.forEach((arg, index) => arithmetic(arg, `${path}.args[${index}]`, depth + 1, references));
      if (node.op === "div" && args[1]?.const === 0) fail("NATIVE_CONDITION_DEPENDENCY", path, "Division by zero in predicate dependency.");
    }
  }
  function scalar(node, path, depth) {
    charge(depth, path); object(node, path);
    if (own(node, "const")) {
      keys(node, ["const", "valueType"], path);
      if (node.valueType !== undefined && !["date", "timestamp"].includes(node.valueType)) fail("NATIVE_CONDITION_TYPE", path, "Literal type annotation must be date or timestamp.");
      if (node.valueType !== undefined && typeof node.const !== "string") fail("NATIVE_CONDITION_TYPE", path, "Date/timestamp literals must be strings.");
      return typed(node.const, node.valueType, path, limits).type;
    }
    if (own(node, "field")) {
      keys(node, ["field", "scope", "valueType"], path); safePath(node.field, path, limits);
      if (!scopes.includes(node.scope) || (options.allowedScopes && !options.allowedScopes.includes(node.scope))) fail("NATIVE_CONDITION_SCOPE", path, "Field scope is unavailable for this target.");
      if (node.scope === "group" && node.field !== "key") fail("NATIVE_CONDITION_SCOPE", path, "Only the current group key is a group scalar.");
      if (node.scope === "cell" && node.field !== "value") fail("NATIVE_CONDITION_SCOPE", path, "Only the current raw cell value is a cell scalar.");
      if (node.valueType !== undefined && !types.includes(node.valueType)) fail("NATIVE_CONDITION_TYPE", path, "Unsupported field type.");
      const declared = fieldType(options.fields, node.scope, node.field);
      if (declared !== undefined && !types.includes(declared)) fail("NATIVE_CONDITION_TYPE", path, "Source contract contains an unsupported scalar type.");
      if (!declared && options.closedScopes?.includes(node.scope)) fail("NATIVE_CONDITION_FIELD", path, "Unknown field in closed source contract.");
      if (declared && node.valueType && declared !== node.valueType) fail("NATIVE_CONDITION_TYPE", path, "Field type conflicts with its source contract.");
      if (!declared) options.onUnresolved?.(path);
      return node.valueType || declared || "unknown";
    }
    keys(node, ["aggregate"], path);
    const spec = node.aggregate; keys(spec, ["fn", "scope", "field", "expr"], `${path}.aggregate`);
    if (!["sum", "count", "min", "max", "avg"].includes(spec.fn) || !["report", "group"].includes(spec.scope)) fail("NATIVE_CONDITION_SCOPE", path, "Aggregate requires an existing function and explicit table/group scope.");
    if (!allowedScopes.includes("dataset") || (spec.scope === "group" && !allowedScopes.includes("group"))) fail("NATIVE_CONDITION_SCOPE", path, "Aggregate scope is unavailable for this target.");
    if (spec.fn !== "count" && (own(spec, "field") === own(spec, "expr"))) fail("NATIVE_CONDITION_INVALID", path, "Aggregate requires one numeric source.");
    if (spec.fn === "count" && (own(spec, "field") || own(spec, "expr"))) fail("NATIVE_CONDITION_INVALID", path, "Count does not accept a source expression.");
    const references = new Set();
    if (own(spec, "field")) { safePath(spec.field, path, limits); references.add(spec.field); }
    if (own(spec, "expr")) arithmetic(spec.expr, `${path}.aggregate.expr`, depth + 1, references);
    for (const ref of references) {
      const declared = fieldType(options.fields, "detail", ref);
      if (!declared && options.closedScopes?.includes("detail")) fail("NATIVE_CONDITION_FIELD", path, "Unknown aggregate source in closed source contract.");
      if (declared && declared !== "number") fail("NATIVE_CONDITION_TYPE", path, "Aggregate source contract must be numeric.");
    }
    return "number";
  }

  function condition(node, path, depth = 1) {
    charge(depth, path); object(node, path);
    if (own(node, "const")) {
      keys(node, ["const"], path);
      if (typeof node.const !== "boolean") fail("NATIVE_CONDITION_TYPE", path, "Predicate root/literal must be boolean.");
      return;
    }
    if (comparisons.includes(node.op)) {
      keys(node, ["op", "left", "right"], path);
      const left = scalar(node.left, `${path}.left`, depth + 1);
      const right = scalar(node.right, `${path}.right`, depth + 1);
      if (!["null", "unknown"].includes(left) && !["null", "unknown"].includes(right) && left !== right) fail("NATIVE_CONDITION_TYPE", path, "Comparison types must match.");
      if (!["eq", "neq"].includes(node.op) && [left, right].some((type) => !["number", "date", "timestamp", "unknown"].includes(type))) fail("NATIVE_CONDITION_TYPE", path, "Ordered comparisons require non-null numbers, dates or timestamps.");
    } else if (["and", "or"].includes(node.op)) {
      keys(node, ["op", "args"], path);
      if (!Array.isArray(node.args) || node.args.length < 2 || node.args.length > limits.operands) fail("NATIVE_CONDITION_INVALID", path, "Boolean groups require two to sixteen predicates.");
      dataArray(node.args, path, limits.operands);
      node.args.forEach((arg, index) => condition(arg, `${path}.args[${index}]`, depth + 1));
    } else if (["not", "isNull", "isNotNull"].includes(node.op)) {
      keys(node, ["op", "arg"], path);
      if (node.op === "not") condition(node.arg, `${path}.arg`, depth + 1);
      else scalar(node.arg, `${path}.arg`, depth + 1);
    } else fail("NATIVE_CONDITION_INVALID", path, "Unsupported predicate operator.");
  }
  return { condition, arithmetic, get nodes() { return nodes; }, limits };
}
function validateCalculations(declarations, options = {}) {
  object(declarations, "$.calculatedFields");
  const ids = Object.keys(declarations);
  const limits = limitOptions(options.limits);
  if (ids.length > limits.definitionNodes) fail("NATIVE_CONDITION_LIMIT", "$.calculatedFields", "Calculated dependency count exceeded.");
  let nodes = 0;
  const positions = new Map(ids.map((id, index) => [id, index]));
  ids.forEach((id, index) => {
    safePath(id, "$.calculatedFields", limits);
    if (id.includes(".")) fail("NATIVE_CONDITION_DEPENDENCY", "$.calculatedFields", "Calculated identities cannot contain path separators.");
    const validator = predicateValidator(options);
    const refs = new Set();
    validator.arithmetic(declarations[id], `$.calculatedFields.${id}`, 1, refs);
    nodes += validator.nodes;
    if (nodes > limits.definitionNodes) fail("NATIVE_CONDITION_LIMIT", "$.calculatedFields", "Calculated dependency nodes exceeded.");
    for (const ref of refs) {
      if (positions.has(ref) && positions.get(ref) >= index) fail("NATIVE_CONDITION_DEPENDENCY", "$.calculatedFields", "Cyclic, self or forward calculated dependency.");
      const declared = fieldType(options.fields, "detail", ref);
      if (declared && declared !== "number") fail("NATIVE_CONDITION_DEPENDENCY", "$.calculatedFields", "Arithmetic source contract must be numeric.");
      if (!positions.has(ref) && !declared && options.closedScopes?.includes("detail")) fail("NATIVE_CONDITION_FIELD", "$.calculatedFields", "Unknown arithmetic source in closed source contract.");
    }
  });
  return nodes;
}
export function validateNativePredicate(predicate, options = {}) {
  try {
    const validator = predicateValidator(options);
    validator.condition(predicate, "$");
    return { errors: [], nodes: validator.nodes };
  } catch (error) {
    if (!(error instanceof NativeConditionError)) throw error;
    return { errors: [diagnostic(error)], nodes: 0 };
  }
}

const emptyCalculations = Object.freeze({});
function evaluationState() { return { work: 0, rows: new WeakMap(), aggregates: new WeakMap() }; }
export function createNativePredicateSession() {
  const state = evaluationState();
  return Object.freeze({
    evaluate: (predicate, context, options) => evaluatePredicate(predicate, context, options || {}, state),
    get work() { return state.work; },
  });
}
export function evaluateNativePredicate(predicate, context = {}, options = {}) {
  return evaluatePredicate(predicate, context, options, evaluationState());
}
export function evaluateNativePredicates(predicate, contexts, options = {}) {
  const limits = limitOptions(options.limits);
  dataArray(contexts, "$.contexts", limits.work);
  const state = evaluationState();
  if (!contexts.length) {
    const validator = predicateValidator(options);
    validator.condition(predicate, "$");
    state.work = validator.nodes;
    if (state.work > limits.work) fail("NATIVE_CONDITION_LIMIT", "$", "Predicate evaluation work limit exceeded.");
  }
  const results = contexts.map((context, sourceIndex) => evaluatePredicate(predicate, context, { ...options, sourceIndex }, state).value);
  return { results, work: state.work };
}
function evaluatePredicate(predicate, context, options, state) {
  try {
    object(context, "$.context");
    const declarations = context.calculatedFields ?? emptyCalculations;
    object(declarations, "$.calculatedFields");
    options = { ...options, fields: { ...options.fields, detail: { ...options.fields?.detail } } };
    for (const id of Object.keys(declarations)) {
      if (fieldType(options.fields, "detail", id) && fieldType(options.fields, "detail", id) !== "number") fail("NATIVE_CONDITION_TYPE", "$.calculatedFields", "Calculated source type conflicts with the numeric contract.");
      options.fields.detail[id] = "number";
    }
    const allowedScopes = Object.keys(context).filter((key) => scopes.includes(key) && context[key] !== undefined);
    if (options.allowedScopes && options.allowedScopes.some((scope) => !allowedScopes.includes(scope))) fail("NATIVE_CONDITION_SCOPE", "$.context", "Evaluation context lacks an admitted scope.");
    const validator = predicateValidator({ ...options, allowedScopes: options.allowedScopes || allowedScopes });
    validator.condition(predicate, "$");
    const limits = validator.limits;
    const initialWork = state.work;
    const charge = (amount = 1) => { state.work += amount; if (state.work > limits.work) fail("NATIVE_CONDITION_LIMIT", "$", "Predicate evaluation work limit exceeded."); };
    charge(validator.nodes + validateCalculations(declarations, options));
    const ids = Object.keys(declarations);
    if (!state.rows.has(declarations)) state.rows.set(declarations, new WeakMap());
    const rowCache = state.rows.get(declarations);
    function numericExpression(expr, row) {
      charge();
      if (own(expr, "field")) {
        const value = readPath(row, expr.field, "$.dependency");
        if (value !== null && (typeof value !== "number" || !Number.isFinite(value))) fail("NATIVE_CONDITION_DEPENDENCY", "$.dependency", "Arithmetic dependency requires a finite numeric source.");
      } else if (!own(expr, "const")) {
        const args = arithmeticArgs(expr);
        args.forEach((arg) => numericExpression(arg, row));
      }
      const result = evaluateNativeExpression(expr, { row });
      if ((result === null && !own(expr, "field")) || (result !== null && !Number.isFinite(result))) fail("NATIVE_CONDITION_DEPENDENCY", "$.dependency", "Arithmetic dependency failed.");
    }
    function evaluatedRow(source) {
      object(source, "$.detail");
      if (rowCache.has(source)) return rowCache.get(source);
      const row = { ...source };
      for (const id of ids) {
        charge();
        numericExpression(declarations[id], row);
        row[id] = evaluateNativeExpression(declarations[id], { row });
      }
      rowCache.set(source, row);
      return row;
    }
    if (!state.aggregates.has(declarations)) state.aggregates.set(declarations, new WeakMap());
    function scalar(node) {
      charge();
      if (own(node, "const")) return typed(node.const, node.valueType, "$.const", limits);
      if (own(node, "field")) {
        const source = node.scope === "detail" ? evaluatedRow(context.detail) : context[node.scope];
        const value = readPath(source, node.field, "$.field");
        return typed(value, node.valueType || fieldType(options.fields, node.scope, node.field), "$.field", { ...limits, literalLength: Number.MAX_SAFE_INTEGER });
      }
      const spec = node.aggregate;
      const cacheKey = JSON.stringify(spec);
      const sourceRows = spec.scope === "group" ? context.groupRows : context.reportRows;
      if (!Array.isArray(sourceRows)) fail("NATIVE_CONDITION_SCOPE", "$.aggregate", "Aggregate rows are unavailable in this context.");
      const aggregateCache = state.aggregates.get(declarations);
      if (!aggregateCache.has(sourceRows)) aggregateCache.set(sourceRows, new Map());
      const aggregates = aggregateCache.get(sourceRows);
      if (aggregates.has(cacheKey)) return aggregates.get(cacheKey);
      dataArray(sourceRows, "$.aggregate", limits.work);
      const rows = sourceRows.map((source) => {
        charge();
        const row = evaluatedRow(source);
        if (spec.expr) numericExpression(spec.expr, row);
        else if (spec.field) {
          const value = readPath(row, spec.field, "$.aggregate.field");
          if (value !== null && (typeof value !== "number" || !Number.isFinite(value))) fail("NATIVE_CONDITION_TYPE", "$.aggregate.field", "Aggregate source must be numeric or null.");
        }
        return row;
      });
      const value = evaluateAggregate(spec, { reportRows: rows, currentGroupId: spec.scope === "group" ? "current" : undefined, groupRowsById: new Map([["current", rows]]) });
      const result = typed(value, "number", "$.aggregate", limits);
      aggregates.set(cacheKey, result);
      return result;
    }
    function evaluate(node) {
      charge();
      if (own(node, "const")) return node.const;
      if (node.op === "and") return node.args.every(evaluate);
      if (node.op === "or") return node.args.some(evaluate);
      if (node.op === "not") return !evaluate(node.arg);
      if (node.op === "isNull") return scalar(node.arg).type === "null";
      if (node.op === "isNotNull") return scalar(node.arg).type !== "null";
      const left = scalar(node.left); const right = scalar(node.right);
      if (["eq", "neq"].includes(node.op) && (left.type === "null" || right.type === "null")) {
        const equal = left.type === right.type;
        return node.op === "eq" ? equal : !equal;
      }
      if (left.type !== right.type || (!["eq", "neq"].includes(node.op) && !["number", "date", "timestamp"].includes(left.type))) fail("NATIVE_CONDITION_TYPE", "$", "Comparison requires matching legal scalar types.");
      if (node.op === "eq") return left.value === right.value;
      if (node.op === "neq") return left.value !== right.value;
      if (node.op === "gt") return left.value > right.value;
      if (node.op === "gte") return left.value >= right.value;
      if (node.op === "lt") return left.value < right.value;
      return left.value <= right.value;
    }
    // Validate declared dependencies even if a boolean branch short-circuits.
    if (context.detail !== undefined) evaluatedRow(context.detail);
    const value = evaluate(predicate);
    return { value, work: state.work - initialWork };
  } catch (error) {
    if (!(error instanceof NativeConditionError)) throw error;
    for (const key of ["ruleId", "elementId", "sourceIndex"]) if (options[key] !== undefined) error[key] = options[key];
    throw error;
  }
}

export function validateConditionalDefinition(payload, options = {}) {
  const warnings = [];
  let warningCount = 0;
  let identity = {};
  try {
    const profile = assertReportAdmission(payload, "validate");
    if (!profile.requiredFeatures.includes(CONDITIONAL_RULES_CAPABILITY)) return { errors: [], warnings, errorCount: 0, warningCount };
    const layout = payload.renderer?.layout || payload.layout;
    if (own(payload, "conditions")) fail("NATIVE_CONDITION_SCOPE", "$.conditions", "Root-level conditional rules are unsupported.");
    const limits = limitOptions(options.limits);
    const used = new Set();
    const cells = new Set();
    const claimed = new Set();
    let count = 0; let nodes = 0;
    const docScopes = ["document"];
    function target(value, path, allowedScopes, kind = "text", visibilityOnly = false, fields = options.fields) {
      if (value === undefined) return;
      object(value, path);
      if (!own(value, "conditions")) return;
      claimed.add(`${path}.conditions`);
      const conditions = value.conditions;
      keys(conditions, ["visibleWhen", "formatting"], `${path}.conditions`);
      if (visibilityOnly && conditions.formatting !== undefined) fail("NATIVE_CONDITION_INVALID", path, "This target supports conditional visibility only.");
      if (conditions.formatting !== undefined && (!Array.isArray(conditions.formatting) || conditions.formatting.length > limits.rulesPerTarget)) fail("NATIVE_CONDITION_LIMIT", path, "Formatting rule count exceeded.");
      const rules = [...(conditions.visibleWhen !== undefined ? [conditions.visibleWhen] : []), ...(conditions.formatting || [])];
      rules.forEach((rule, index) => {
        const rulePath = `${path}.conditions.${index === 0 && conditions.visibleWhen !== undefined ? "visibleWhen" : `formatting[${index - (conditions.visibleWhen !== undefined ? 1 : 0)}]`}`;
        const formatting = rule !== conditions.visibleWhen;
        keys(rule, formatting ? ["id", "enabled", "when", "style"] : ["id", "enabled", "when"], rulePath);
        identity = { ruleId: rule.id, elementId: value.id };
        if (typeof rule.id !== "string" || !safeId.test(rule.id) || used.has(rule.id)) fail("NATIVE_CONDITION_INVALID", `${rulePath}.id`, "Rule identities must be safe, nonempty and document-unique.");
        used.add(rule.id);
        if (++count > limits.rules) fail("NATIVE_CONDITION_LIMIT", rulePath, "Definition rule count exceeded.");
        if (rule.enabled !== undefined && typeof rule.enabled !== "boolean") fail("NATIVE_CONDITION_INVALID", rulePath, "Rule enabled must be boolean.");
        const validator = predicateValidator({ ...options, fields, allowedScopes, onUnresolved: (fieldPath) => {
          warningCount++;
          if (warnings.length < 20) warnings.push({ code: "NATIVE_CONDITION_FIELD", path: `${rulePath}.when${fieldPath.slice(1)}`, message: "Raw field requires runtime/source-contract validation." });
        } });
        validator.condition(rule.when, `${rulePath}.when`);
        nodes += validator.nodes;
        if (nodes > limits.definitionNodes) fail("NATIVE_CONDITION_LIMIT", rulePath, "Definition predicate-node limit exceeded.");
        if (formatting) {
          keys(rule.style, ["color", "backgroundColor", "bold", "italic", "borderColor", "borderWidth"], `${rulePath}.style`);
          if (!Object.keys(rule.style).length) fail("NATIVE_CONDITION_INVALID", rulePath, "Formatting rule requires a style override.");
          for (const [key, value] of Object.entries(rule.style)) {
            if (["color", "backgroundColor", "borderColor"].includes(key) && (typeof value !== "string" || !/^#[0-9A-Fa-f]{6}$/.test(value))) fail("NATIVE_CONDITION_INVALID", rulePath, "Conditional colors require opaque #RRGGBB.");
            if (["bold", "italic"].includes(key) && (typeof value !== "boolean" || ["image", "container"].includes(kind))) fail("NATIVE_CONDITION_INVALID", rulePath, "Invalid conditional font emphasis for this target.");
            if (key === "color" && ["image", "container"].includes(kind)) fail("NATIVE_CONDITION_INVALID", rulePath, "Text color is unsupported on this target.");
            if (key === "borderWidth" && (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 3)) fail("NATIVE_CONDITION_INVALID", rulePath, "Conditional border width must be 0-3 points.");
          }
        }
        identity = {};
      });
    }
    function array(value, path) {
      if (value === undefined) return [];
      return dataArray(value, path);
    }
    function summaries(region, path, allowedScopes, fields) {
      target(region, path, allowedScopes, "text", false, fields);
      for (const [index, cell] of array(region?.cells, `${path}.cells`).entries()) {
        object(cell, `${path}.cells[${index}]`);
        if (own(cell, "conditions")) {
          if (typeof cell.id !== "string" || !safeId.test(cell.id) || cells.has(cell.id)) fail("NATIVE_CONDITION_INVALID", `${path}.cells[${index}]`, "Conditional summary cells require unique stable identities.");
          cells.add(cell.id);
        }
        target(cell, `${path}.cells[${index}]`, [...allowedScopes, "cell"], "text", false, fields);
      }
    }
    function elements(values, path, inherited, depth = 0) {
      for (const [index, element] of array(values, path).entries()) {
        object(element, path);
        const location = `${path}[${index}]`;
        if (!["text", "field", "container", "image", "table"].includes(element.type) && own(element, "conditions")) fail("NATIVE_CONDITION_INVALID", location, "Unsupported conditional element.");
        const tableScopes = ["document", "dataset"];
        target(element, location, element.type === "table" ? tableScopes : inherited, element.type, element.type === "table");
        if (element.type === "container") elements(element.elements, `${location}.elements`, inherited, depth + 1);
        if (element.type !== "table") continue;
        if (depth) fail("NATIVE_CONDITION_SCOPE", location, "Conditional profile does not support nested tables.");
        const grouped = Array.isArray(element.groups) && element.groups.length > 0;
        if (element.groups?.length > 1) fail("NATIVE_CONDITION_SCOPE", location, "Nested grouping is not supported.");
        const detailScopes = [...tableScopes, ...(grouped ? ["group"] : []), "detail"];
        const calculations = Object.create(null);
        const fields = { ...options.fields, detail: { ...options.fields?.detail } };
        const columns = array(element.columns, `${location}.columns`);
        for (const column of columns) {
          object(column, `${location}.columns`);
          if (column.expr !== undefined || column.calculation !== undefined) {
            const id = column.id || column.key || column.binding;
            safePath(id, `${location}.columns`, limits);
            if (own(calculations, id)) fail("NATIVE_CONDITION_DEPENDENCY", location, "Duplicate calculated column identity.");
            calculations[id] = column.expr || column.calculation;
            fields.detail[id] = "number";
          }
        }
        if (columns.some(column => column.expr?.typed !== undefined)) {
          const plan = compileTypedReportTargets({ ...payload, layout }, [element]).tablePlans.get(element.id);
          Object.entries(plan.types).forEach(([id, type]) => { fields.detail[id] = type.type; });
          nodes += plan.nodes;
        } else nodes += validateCalculations(calculations, { ...options, fields });
        if (nodes > limits.definitionNodes) fail("NATIVE_CONDITION_LIMIT", location, "Definition calculation-node limit exceeded.");
        target(element.header, `${location}.header`, tableScopes, "text", false, fields);
        target(element.row, `${location}.row`, detailScopes, "text", false, fields);
        for (const [columnIndex, column] of columns.entries()) {
          target(column, `${location}.columns[${columnIndex}]`, tableScopes, "text", true);
          if (column.detail !== undefined) keys(column.detail, ["conditions"], `${location}.columns[${columnIndex}].detail`);
          target(column.detail, `${location}.columns[${columnIndex}].detail`, [...detailScopes, "cell"], "text", false, fields);
        }
        summaries(element.footer, `${location}.footer`, tableScopes, fields);
        for (const [groupIndex, group] of array(element.groups, `${location}.groups`).entries()) {
          object(group, `${location}.groups[${groupIndex}]`);
          target(group.header, `${location}.groups[${groupIndex}].header`, [...tableScopes, "group"], "text", false, fields);
          summaries(group.footer, `${location}.groups[${groupIndex}].footer`, [...tableScopes, "group"], fields);
        }
      }
    }
    for (const [index, band] of (layout.bands || []).entries()) {
      object(band, `$.layout.bands[${index}]`);
      if (!["reportHeader", "pageHeader", "detail", "reportFooter", "pageFooter"].includes(band.role)) {
        if (own(band, "conditions")) fail("NATIVE_CONDITION_SCOPE", `$.layout.bands[${index}]`, "Unsupported conditional band.");
        continue;
      }
      const allowed = band.role === "detail" && band.dataset ? ["document", "detail"] : docScopes;
      target(band, `$.layout.bands[${index}]`, allowed, "text", true);
      elements(band.elements, `$.layout.bands[${index}].elements`, allowed);
    }
    function unclaimed(value, path) {
      if (!value || typeof value !== "object") return;
      if (Array.isArray(value)) {
        value.forEach((child, index) => unclaimed(child, `${path}[${index}]`));
        return;
      }
      for (const [key, child] of Object.entries(value)) {
        if (key === "conditions" && !claimed.has(`${path}.conditions`)) fail("NATIVE_CONDITION_SCOPE", path, "Conditional properties are not supported on this target.");
        unclaimed(child, `${path}.${key}`);
      }
    }
    unclaimed(layout, "$.layout");
    if (payload.renderer && payload.bands) unclaimed(payload.bands, "$.bands");
    return { errors: [], warnings, errorCount: 0, warningCount };
  } catch (error) {
    if (!(error instanceof NativeConditionError)) throw error;
    for (const key of ["ruleId", "elementId"]) if (typeof identity[key] === "string" && safeId.test(identity[key])) error[key] = identity[key];
    return { errors: [diagnostic(error)], warnings, errorCount: 1, warningCount };
  }
}
